import { resolveArEndpoint } from "./ar-launch-dock";

export interface ArQrCodeUploadOptions {
  /**
   * Same-origin path (or absolute https URL, for direct CPQ embed where this script runs on
   * CPQ's own document) to the QR-upload endpoint. Default `/api/ar/qrUrl`.
   */
  qrUrlEndpoint?: string;
  /** Merged into `player.saveConfiguration({ metadata })` (default `{ source: "pride-cpq-ar" }`). */
  metadata?: Record<string, unknown>;
  /**
   * Threekit env subdomain the AR link (and the QR image upload) should target. Defaults to the
   * `env` the player was mounted with.
   */
  env?: string;
  /**
   * Org id that, with `env`, selects the environment (dev / staging / prod) — required to tell
   * staging and prod apart on `admin-fts`. Defaults to the `orgId` the player was mounted with.
   */
  orgId?: string;
}

export interface ArQrCodeUploadResult {
  /** Hosted https URL of the uploaded QR code image (Threekit Files API content URL). */
  url: string;
  /** The AR landing URL this QR code encodes. */
  targetUrl: string;
  /** Saved configuration `shortId` used to build the AR link. */
  shortId: string;
}

type PlayerWithSaveConfiguration = {
  saveConfiguration?: (opts?: {
    metadata?: Record<string, unknown>;
  }) => { shortId: string } | Promise<{ shortId: string }>;
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Saves the current configuration, then asks the server (`GET /api/ar/qrUrl`, see
 * `server/ar-service.mjs`) to generate the AR QR code and upload it to Threekit's Files API —
 * returning a stable https URL you can persist (e.g. `CPQJS.setAttributeVal(...)`) and reuse
 * later (an emailed quote, a saved record) without regenerating the QR code on demand.
 */
export async function generateArQrCodeAndUpload(args: {
  env: string;
  orgId?: string;
  assetId: string;
  player: unknown;
  options?: ArQrCodeUploadOptions;
}): Promise<ArQrCodeUploadResult> {
  const { assetId, player, options = {} } = args;
  const env = options.env?.trim() || args.env;
  const orgId = options.orgId?.trim() || args.orgId?.trim() || "";

  if (!assetId) {
    throw new Error("generateArQrCodeAndUpload: assetId is required");
  }

  const p = player as PlayerWithSaveConfiguration;
  if (typeof p.saveConfiguration !== "function") {
    throw new Error(
      "generateArQrCodeAndUpload: player.saveConfiguration is not available (Threekit player version)"
    );
  }

  const { shortId } = await p.saveConfiguration({
    metadata: options.metadata ?? { source: "pride-cpq-ar" },
  });

  const endpoint = resolveArEndpoint(options.qrUrlEndpoint?.trim() || "/api/ar/qrUrl");
  const params = new URLSearchParams({ shortId, assetId, environment: env });
  if (orgId) params.set("orgId", orgId);

  const res = await fetch(`${endpoint}?${params.toString()}`);
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(
      `generateArQrCodeAndUpload: server returned non-JSON (HTTP ${res.status})`
    );
  }

  if (!res.ok) {
    const message =
      isRecord(json) && typeof json.error === "string" ? json.error : text.slice(0, 500);
    throw new Error(`generateArQrCodeAndUpload: HTTP ${res.status}: ${message}`);
  }

  const url = isRecord(json) ? json.url : undefined;
  if (typeof url !== "string" || !url) {
    throw new Error("generateArQrCodeAndUpload: server response has no url");
  }
  const targetUrl = isRecord(json) && typeof json.targetUrl === "string" ? json.targetUrl : "";

  return { url, targetUrl, shortId };
}
